import { Voucher } from "@/lib/redux/features/voucher/type"
import {
  VoucherApiV3,
  VoucherCategoryApiV3,
  VouchersGetAllResponseV3,
} from "@/lib/redux/features/voucher/voucherApiV3.types"

const toStr = (v: unknown, fallback = ""): string =>
  v == null || v === "" ? fallback : String(v)

/** Mã hiển thị / áp dụng — chỉ từ `codes`; `voucherCode` API không dùng. Không có codes → bỏ voucher. */
export const resolveVoucherCodes = (raw: VoucherApiV3): string[] =>
  (raw.codes ?? []).map((c) => String(c).trim()).filter(Boolean)

const parseMaxDiscount = (conditions?: VoucherApiV3["conditions"]) => {
  const row = conditions?.find((c) => c.conditionType === "MAX_DISCOUNT_VALUE" && c.isEnable !== false)
  if (row?.value == null) return undefined
  const n = Number(row.value)
  return Number.isFinite(n) ? n : undefined
}

const parseAutoApply = (conditions?: VoucherApiV3["conditions"]) => {
  return !!conditions?.some(
    (c) => c.conditionType === "AUTO_APPLY"
  )
}

/** Voucher có điều kiện IS_HIDDEN → không hiển thị trên FE. */
export const isHiddenVoucher = (raw: VoucherApiV3): boolean =>
  !!raw.conditions?.some((c) => c.conditionType === "IS_HIDDEN")

export const normalizeVoucher = (
  raw: VoucherApiV3,
  category?: VoucherCategoryApiV3,
  promoCode?: string,
): Voucher => {
  const voucherCode = promoCode ?? ""
  const valueType = (raw.valueType === "PERCENT" ? "PERCENT" : "AMOUNT") as Voucher["valueType"]
  const numericValue =
    typeof raw.value === "number" ? raw.value : parseFloat(String(raw.value ?? ""))

  const defaultAmount =
    raw.defaultAmount != null
      ? toStr(raw.defaultAmount)
      : valueType === "AMOUNT" && Number.isFinite(numericValue)
        ? String(numericValue)
        : "0"

  return {
    voucherId: toStr(raw.voucherId),
    voucherCode,
    displayName: toStr(raw.displayName),
    voucherCategoryCode: toStr(raw.voucherCategoryCode || category?.voucherCategoryCode),
    isEnable: raw.isEnable !== false,
    defaultAmount,
    voucherQuantity: toStr(raw.voucherQuantity ?? "1"),
    createdDate: toStr(raw.createdDate),
    startDate: toStr(raw.startDate),
    endDate: toStr(raw.endDate),
    initMethod: toStr(raw.initMethod || category?.initMethod),
    value: toStr(raw.value ?? (valueType === "AMOUNT" ? defaultAmount : "")),
    valueType,
    status: toStr(raw.status, "Active"),
    productType: raw.productType as Voucher["productType"],
    remainCounter: raw.remainCounter,
    remainAmount: raw.remainAmount != null ? toStr(raw.remainAmount) : undefined,
    voucherCodeType: raw.voucherType || raw.voucherCodeType,
    clientId: raw.clientId || category?.clientId,
    categoryName: category?.categoryName,
    categoryType: category?.categoryType,
    maxDiscountValue: parseMaxDiscount(raw.conditions),
    autoApply: parseAutoApply(raw.conditions),
    showType: "media",
  }
}

export const normalizeVoucherCategories = (
  categories?: VoucherCategoryApiV3[] | null,
): Voucher[] => {
  if (!categories?.length) return []

  return categories.flatMap((category) =>
    (category.vouchers || [])
      .filter((v) => !isHiddenVoucher(v))
      .flatMap((v) =>
        resolveVoucherCodes(v).map((code) => normalizeVoucher(v, category, code)),
      ),
  )
}

export const normalizeVouchersGetAllResponse = (
  payload?: VouchersGetAllResponseV3 | Voucher[] | null,
): Voucher[] => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  return normalizeVoucherCategories(payload.categories)
}
